import { FocusEvent, KeyboardEvent, useRef, useState } from 'react';
import { hasDateChanged } from '../../../../../utils/dates';
import { UseInlineEditDateTimePickerParams } from '../inlineEditDateTimePicker.types';

const isIncompleteDate = (date: Date | null) => date !== null && Number.isNaN(date.getTime());

export const useInlineEditDateTimePicker = ({
  value,
  isDisabled,
  validate,
  onConfirm,
}: UseInlineEditDateTimePickerParams) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<Date | null>(value);
  const [error, setError] = useState<string>();

  const stopEditing = () => {
    setIsEditing(false);
    setIsPopupOpen(false);
  };

  const handleStartEditing = () => {
    if (isDisabled) {
      return;
    }

    setError(undefined);
    setDraftValue(value);
    setIsEditing(true);
  };

  const handleChangeDraft = (nextValue: Date | null) => {
    setError(undefined);
    setDraftValue(nextValue);
  };

  const handleCancel = () => {
    setError(undefined);
    setDraftValue(value);
    stopEditing();
  };

  const handleConfirm = (nextValue: Date | null) => {
    if (isIncompleteDate(nextValue)) {
      handleCancel();
      return;
    }

    const validationError = validate?.(nextValue);

    if (validationError) {
      setError(validationError);
      setDraftValue(nextValue);
      return;
    }

    stopEditing();

    if (!hasDateChanged(nextValue, value)) {
      return;
    }

    onConfirm(nextValue);
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (isPopupOpen || containerRef.current?.contains(event.relatedTarget)) {
      return;
    }

    handleConfirm(draftValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      handleConfirm(draftValue);
      return;
    }

    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  return {
    containerRef,
    error,
    isEditing,
    isPopupOpen,
    draftValue,
    handleStartEditing,
    handleChangeDraft,
    handleConfirm,
    handleBlur,
    handleKeyDown,
    openPopup: () => setIsPopupOpen(true),
    closePopup: () => setIsPopupOpen(false),
  };
};
